import {
    AfterContentChecked, ChangeDetectionStrategy, ChangeDetectorRef,
    Component, ContentChild,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    SimpleChanges,
    signal,
    computed
} from '@angular/core';
import { ColumnComponent } from './column/column.component';
import { FilterItemModel } from '../../../core/models/misc/filter-item.model';
import { FilterDialogComponent } from '../../dialogs/filter-dialog/filter-dialog.component';
import { DialogService } from '../../../service/services/misc/dialog/dialog.service';
import { MenuComponent } from '@fundamental-ngx/core';
import { PaginationModel } from './pagination.model';
import { ResourceModel } from '../../../core/models/resources/resource.model';
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import {SortByPipe} from "../../pipes/sort/sort-pipe";
import {FilterByFilterItemsPipe} from "../../pipes/filter-by-filter-items/filter-by-filter-items.pipe";
import {DynamicPipe} from "../../pipes/dynamic/dynamic.pipe";
import {SortByFixedPipe} from "../../pipes/sort-by-fixed/sort-by-fixed.pipe";
import {LocalStorageService} from "../../../service/services/misc/local-storage/local-storage.service";

export class PersistencyData {
    data: any;
    additionalData: any;

    constructor(init?: Partial<PersistencyData>) {
        Object.assign(this, init);
    }
}

@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DataTableComponent implements OnInit, OnChanges, AfterContentChecked {
    @Input() isResourcesTable = true;
    @Input() isClientSideSortingEnabled = true;
    @Input() tableKey: string;
	@Input() dataset: any[];
	@Input() expandableContent: TemplateRef<any>;
	@Input() noDataMessage: string;
    @ContentChild('noDataMessageTemplate') noDataMessageTemplate: TemplateRef<any>;
	@Input() noDataFilteredMessage: string;
	@Input() filterPipe: any;
	@Input() filterPipeArgs: string[];
	@Input() stickyHeaders = false;
	@Input() hoverable = false;
	@Input() clickable = false;
	@Input() selectedRow: any;
	@Input() loading = false;
	@Input() sortProperty: string;
	@Input() pagination: PaginationModel;
	@Input() displayedColumns: string[];
	@Output() rowClick: EventEmitter<any> = new EventEmitter<any>();
    @Output() scrolledIndexChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() sortChange: EventEmitter<{ asc: boolean; prop: string; }> = new EventEmitter<{asc: boolean; prop: string}>();
	@Input() fixedSortingBy: string;
    @Input() displayLoadingText = "Loading...";
    @Input() displayFooterText = "";

    @Input() virtualScrollingEnabled = false;

    @Input() virtualScrollingItemSize = 44;
    @Input() persistSort = false;
    @Input() persistSortAdditionalData: any;
    @Input() persistFilters = false;
    @Input() persistFiltersAdditionalData: any;

    @ViewChild('tableViewPort')
        tableViewPort: CdkVirtualScrollViewport;

    initialSortProperty: string;
    @Input() ascending = true;
    sortFn: (a: any, b: any, ascending: boolean) => number;
    hasFilter = false;

    filterItems: FilterItemModel[] = [];

    columns: ColumnComponent[] = [];

	@ViewChild('itemsPerPageMenu')
	    itemsPerPageMenu: MenuComponent;

  @ViewChild('tbody')
      tbody: ElementRef;

  expandedChildrenRows: Set<string> = new Set();
  /** Parents seen in the previous dataset, so newly arriving ones can be auto-expanded. */
  private knownParentUids: Set<string> = new Set();


  constructor(private _dialogService: DialogService, private _cdRef: ChangeDetectorRef, private sortPipe: SortByPipe,
              private filterByFilterItemsPipe: FilterByFilterItemsPipe, private dynamicPipe: DynamicPipe,
              private sortByFixedPipe: SortByFixedPipe, private localStorageService: LocalStorageService) {}

  ngOnInit() {
      this.initialSortProperty = this.sortProperty;
      if (this.tableKey) {
          const isSorted = this.persistSort ? this.initSortPersistency() : false;
          const isFiltered = this.persistFilters ? this.initFiltersPersistency() : false;
          //Workaround to fix change detection issue
          setTimeout(() => {
              this.hasFilter = isSorted || isFiltered;
          }, 0);
      }
  }

  ngOnChanges(changes: SimpleChanges) {
      if ((changes['tableKey'] && !changes['tableKey'].firstChange) ||
          (changes['persistSortAdditionalData'] && !changes['persistSortAdditionalData'].firstChange) ||
          (changes['persistFiltersAdditionalData'] && !changes['persistFiltersAdditionalData'].firstChange)) {
          this.knownParentUids.clear();
          this.expandedChildrenRows.clear();
      }
      if (changes['dataset']?.currentValue) {
          this.syncExpandedChildrenRows();
      }
  }


  ngAfterContentChecked() {
      // Removed: this._cdRef.detectChanges() / markForCheck() caused infinite CD loop
      // with FdNGX 0.63 signal-based ButtonComponent inside fd-popover.
      // The data-table renders correctly without forced CD.
  }

  addColumn(column: ColumnComponent) {
	    this.columns.push(column);
  }

  initSortPersistency(): boolean {
      try {
          const values = this.getSortPersistencyCache();
          if (values.data) {
              this.ascending = values.data.asc;
              this.sortProperty = values.data.prop;
              return true;
          }
      } catch {
          //do nothing
      }
      return false;
  }

  initFiltersPersistency(): boolean {
      try {
          const values = this.getFilterPersistencyCache();
          this.filterItems = values.data || [];
          if (this.filterItems?.length > 0) {
              return true;
          }
      } catch {
          //do nothing
      }
      return false;
  }

  sort(sortObject: any) {
      const { asc, prop, sortFn } = sortObject;
      this.sortChange.emit({
          asc: asc,
          prop: prop
      });
      this.ascending = asc;
      this.sortProperty = prop;
      this.sortFn = sortFn;
      this.hasFilter = true;
      this.newPageClicked(1);
      if (this.persistSort && this.tableKey) {
          const persistencyKey = this.getSortPersistencyKey();
          const persistencyData = new PersistencyData({data: { prop, asc }, additionalData: this.persistSortAdditionalData});
          this.localStorageService.set(persistencyKey, JSON.stringify(persistencyData));
      }
  }

  getSortPersistencyKey() {
      return `${this.tableKey}-sort`;
  }

  getFilterPersistencyKey() {
      return `${this.tableKey}-filter`;
  }

  getSortPersistencyCache(): PersistencyData {
      return JSON.parse(this.localStorageService.get(this.getSortPersistencyKey())) as PersistencyData;
  }

  getFilterPersistencyCache(): PersistencyData {
      return JSON.parse(this.localStorageService.get(this.getFilterPersistencyKey())) as PersistencyData;
  }
  filter(property: string, title: string) {
      // need to add children items to the filter
      const flattenedDataset = [...this.dataset];
      this.dataset.forEach(row => {
          if (this.hasChildren(row)) {
              flattenedDataset.push(...row.children);
          }
      });

      this._dialogService
          .openSideDialog(FilterDialogComponent, {
              property,
              title,
              currentFilters: this.filterItems,
              values: flattenedDataset,
          })
          .afterClosed.subscribe({
              next: (res: any) => {
                  this.filterItems = res.filterItems;
                  this.newPageClicked(1);
                  this.hasFilter = true;

                  if (this.persistFilters && this.tableKey) {
                      const persistencyKey = this.getFilterPersistencyKey();
                      const persistencyData = new PersistencyData({data: this.filterItems, additionalData: this.persistFiltersAdditionalData});
                      this.localStorageService.set(persistencyKey, JSON.stringify(persistencyData));
                  }
              },
          });
  }

  newPageClicked(pageNumber: number): void {
	    if (!this.pagination) {
	        return;
	    }
	    this.pagination.currentPage = pageNumber;
	    if (this.itemsPerPageMenu) {
	        this.itemsPerPageMenu.close();
	    }
  }

  itemsPerPageChange(value: number): void {
	    if (!this.pagination) {
	        return;
	    }
	    this.pagination.itemsPerPage = value;
	    this.newPageClicked(1);
  }

  reset() {
      this.ascending = true;
      this.sortProperty = this.initialSortProperty;
      this.filterItems = [];
      this.hasFilter = false;
      this.newPageClicked(1);
      if (this.tableKey) {
          const localStorageSortKey = this.getSortPersistencyKey();
          const localStorageFilterKey = this.getFilterPersistencyKey();
          this.localStorageService.remove(localStorageSortKey);
          this.localStorageService.remove(localStorageFilterKey);
      }
  }

  currentFirstItem(): any {
	    return this.pagination.itemsPerPage * (this.pagination.currentPage - 1);
  }
  compareByUID(selectedRow: ResourceModel, row: ResourceModel): boolean {
      return (selectedRow?.uid === row?.uid);
  }
  isSelected(row: ResourceModel): boolean {
      return this.selectedRow && row &&
        this.compareByUID(this.selectedRow, row);
  }

  onClickRow(row: any) {
	    this.rowClick.emit(row);
  }
  virtualScrollToRowByProp(property: string, value: any, offset = 0) {
      const selectedIndex = this.getPipedDataset().findIndex(elem => elem[property] === value);
      if(selectedIndex > -1){
          this.tableViewPort.scrollToIndex(selectedIndex + offset);
      }
  }

  virtualScrollToIndex(index: number) {
      this.tableViewPort.scrollToIndex(index);
  }

  getPipedDataset(): any[] {
      return this.sortByFixedPipe
          .transform(this.sortPipe
              .transform(this.dynamicPipe
                  .transform(this.filterByFilterItemsPipe
                      .transform(this.dataset, this.filterItems),
                  this.filterPipe, this.filterPipeArgs), this.ascending, this.sortProperty, this.sortFn), this.ascending, this.fixedSortingBy);
  }

  public get inverseTranslation(): string {
      if (!this.tableViewPort || !this.tableViewPort["_renderedContentOffset"]) {
          return "-0px";
      }
      let offset = this.tableViewPort["_renderedContentOffset"];
      return `-${offset}px`;
  }

  onScrolledIndexChange($event: number) {
      this.scrolledIndexChange.emit($event);
  }

  identifyColumn(index: any, item: any){
      return item.value;
  }

  identifyItem(index: any, item: any){
      return index;
  }

  trackByColumn(index: any, item: any){
      return item;
  }

  hasChildren(row: any): boolean {
      return Array.isArray(row.children) && row.children.length > 0;
  }

  toggleChildRow(row: any, event: Event) {
      event.stopPropagation();
      const rowId = row.uid;
      if (this.expandedChildrenRows.has(rowId)) {
          this.expandedChildrenRows.delete(rowId);
      } else {
          this.expandedChildrenRows.add(rowId);
      }
      this._cdRef.detectChanges();
  }

  isChildRowExpanded(row: any): boolean {
      return this.expandedChildrenRows.has(row.uid);
  }

  /**
   * Resets the expansion state and initializes expandable rows from scratch for a new context.
   */
  initializeExpandedChildrenRows() {
      if (!this.dataset) return;
      this.expandedChildrenRows.clear();
      this.knownParentUids.clear();
      this.syncExpandedChildrenRows();
  }

  /**
   * Auto-expands parents that were not in the previous dataset and drops rows that
   * are gone, so a dataset refresh (polling, paging, navigation) keeps whatever the
   * user expanded or collapsed in between.
   */
  syncExpandedChildrenRows() {
      const parentUids = new Set<string>(
          (this.dataset ?? []).filter(row => this.hasChildren(row)).map(row => row.uid)
      );
      parentUids.forEach(uid => {
          if (!this.knownParentUids.has(uid)) {
              this.expandedChildrenRows.add(uid);
          }
      });
      this.expandedChildrenRows.forEach(uid => {
          if (!parentUids.has(uid)) {
              this.expandedChildrenRows.delete(uid);
          }
      });
      this.knownParentUids = parentUids;
  }
}
